/* eslint-disable no-underscore-dangle */
import { type Client, FilestackError } from 'filestack-js';
import { useCallback, useRef } from 'react';

import { useAppConfig } from '#/config/AppConfigContext';
import { log } from '#/utils/logging';

import { getImageFileDimensions } from './getImageFileDimensions';
import type {
  ApiImageData,
  OnBeforeUploadResult,
  UploadHookParams,
  UploadHookResult,
} from './types';

const importFilestack = () =>
  import(
    /* webpackChunkName: "filestack" */
    'filestack-js'
  );

// This file type is not directly exposed by filestack, esp. the _file property
// on which we have been relying so far. The goal of the function below and this
// type is to hide that detail from the rest of the codebase.
type FilestackUploadResult = {
  _file: {
    name: string;
    size: number;
    type: string;
  };
  status: string;
  handle: string;
  url: string;
};

const convertFilestackUploadResultToApiImageData = (
  result: FilestackUploadResult,
): ApiImageData => ({
  handle: result.handle,
  filename: result._file.name,
  mimetype: result._file.type,
  size: result._file.size,
  // 'local_file_system' is the only source we're using currently (March '25)
  source: 'local_file_system',
});

export const useFilestackUpload = ({
  onBeforeUploadBegin,
  onUploadComplete,
  onUploadError,
  onUploadCancelled,
}: UploadHookParams): UploadHookResult => {
  const { appConfig } = useAppConfig();
  const { apiKey } = appConfig.filestack;

  const cachedClient = useRef<Client | null>(null);
  const getClient = useCallback(async () => {
    if (cachedClient.current) return cachedClient.current;

    const m = await importFilestack();
    if (typeof apiKey !== 'string') throw new Error('Missing API Key');

    cachedClient.current = m.init(apiKey);
    return cachedClient.current;
  }, [apiKey]);

  const upload = useCallback(
    async (file: File) => {
      try {
        const client = await getClient();

        const beforeResult: OnBeforeUploadResult = onBeforeUploadBegin
          ? await onBeforeUploadBegin?.(file)
          : { type: 'continue', file };

        if (beforeResult.type === 'cancel')
          return onUploadCancelled?.('user_cancelled');

        if (beforeResult.type === 'error')
          return onUploadError(beforeResult.error);

        // NOTE: client.upload is not strictly typed, the return type is `any`
        const apiResult = await client
          .upload(beforeResult.file)
          .then(convertFilestackUploadResultToApiImageData);

        try {
          const { width, height } = await getImageFileDimensions(
            beforeResult.file,
          );
          apiResult.width = width;
          apiResult.height = height;
        } catch (error) {
          log.error(error);
        }

        onUploadComplete(apiResult);
      } catch (error: unknown) {
        if (error instanceof FilestackError)
          return onUploadError(new Error(error.message));
        if (error instanceof Error) return onUploadError(error);
        if (typeof error === 'string') return onUploadError(new Error(error));

        // NOTE: this is a potential logic change from the previous version of
        // the picker
        return onUploadError(new Error(`Unknown error: ${String(error)}`));
      }
    },
    [
      getClient,
      onBeforeUploadBegin,
      onUploadCancelled,
      onUploadComplete,
      onUploadError,
    ],
  );

  return { upload };
};
